import { useEffect, useState, useRef, useCallback } from 'react';
import { X, MapPin, Navigation } from 'lucide-react';
import { useMapStore } from '../../stores/mapStore';
import { MAP_DEFAULT_ZOOM } from '../map/config/mapConfig';
import { mapPositionToCoordinates } from '../../utils/geolocation';
import { requestCurrentPosition, watchPermissionState } from '../../utils/geolocationRequest';

export const LOCATION_PROMPT_DISMISSED_KEY = 'location-prompt-dismissed';

// Session-only key — resets every visit so the pill comes back
const SESSION_DISMISSED_KEY = '__location_card_dismissed';

type Platform = 'ios' | 'android' | 'desktop';

function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'desktop';
}

const LOCATION_INSTRUCTIONS: Record<Platform, Array<{ step: string }>> = {
  ios: [
    { step: 'Make sure Location Services are enabled on your device (Settings → Privacy & Security → Location Services)' },
    { step: 'Open Settings and allow location access for Safari' },
    { step: 'Return to the browser and refresh the page' },
  ],
  android: [
    { step: 'Tap the lock or site icon in the browser address bar' },
    { step: 'Open Site settings' },
    { step: 'Set Location permission to "Allow"' },
    { step: 'Refresh the page' },
  ],
  desktop: [
    { step: 'Open System Settings → Privacy & Security → Location Services and turn it on' },
    { step: 'In the list, enable location for your browser (Safari, Chrome, Arc, etc.)' },
    { step: 'Click the lock or site icon beside the address bar and allow Location for this site' },
    { step: 'Return here and tap Enable Location again — no refresh needed' },
  ],
};

interface LocationPermissionPromptProps {
  open: boolean;
  onDismiss: () => void;
}

// ── Draggable pill hook ──
function useDraggable(initialPos: { x: number; y: number }) {
  const [pos, setPos] = useState(initialPos);
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const pillRef = useRef<HTMLDivElement>(null);
  const hasMoved = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    dragging.current = true;
    hasMoved.current = false;
    offset.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [pos]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    hasMoved.current = true;
    const newX = e.clientX - offset.current.x;
    const newY = e.clientY - offset.current.y;
    // Clamp within viewport
    const maxX = window.innerWidth - (pillRef.current?.offsetWidth || 160);
    const maxY = window.innerHeight - (pillRef.current?.offsetHeight || 40);
    setPos({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, []);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  return { pos, pillRef, hasMoved, onPointerDown, onPointerMove, onPointerUp };
}

export function LocationPermissionPrompt({ open, onDismiss }: LocationPermissionPromptProps) {
  const [permState, setPermState] = useState<'prompt' | 'denied' | 'unavailable' | 'unsupported' | 'unknown'>('unknown');
  const [locating, setLocating] = useState(false);
  const [collapsed, setCollapsed] = useState(false); // card → pill
  const { setUserLocation, flyToLocation } = useMapStore();

  // Pill positioned top-right, below search bar (~70px from top, 12px from right)
  const drag = useDraggable({ x: window.innerWidth - 172, y: 70 });

  const applyLocatedPosition = useCallback(
    (position: GeolocationPosition) => {
      const location = mapPositionToCoordinates(position);
      setUserLocation(location);
      flyToLocation(location, MAP_DEFAULT_ZOOM);
      setLocating(false);
      onDismiss();
    },
    [flyToLocation, onDismiss, setUserLocation],
  );

  useEffect(() => {
    if (!open) return;
    // Check if card was already dismissed this session → start as pill
    if (sessionStorage.getItem(SESSION_DISMISSED_KEY)) {
      setCollapsed(true);
    }
    return watchPermissionState((state) => {
      if (state === 'denied') setPermState('denied');
      else if (state === 'granted') setPermState('prompt');
      else setPermState('prompt');
    });
  }, [open]);

  if (!open) {
    return null;
  }

  const handleNotNow = () => {
    // Session-only dismiss: collapses card to pill, comes back next visit
    sessionStorage.setItem(SESSION_DISMISSED_KEY, '1');
    setCollapsed(true);
  };

  const handleEnableLocation = () => {
    if (!navigator.geolocation) {
      setPermState('unsupported');
      return;
    }
    setLocating(true);
    requestCurrentPosition()
      .then(applyLocatedPosition)
      .catch((err: GeolocationPositionError) => {
        setLocating(false);
        switch (err.code) {
          case 1:
            setPermState('denied');
            break;
          case 2:
          case 3:
            setPermState('unavailable');
            break;
          default:
            setPermState('unavailable');
        }
      });
  };

  // ── Draggable persistent pill (top-right below search bar) ──
  if (collapsed) {
    return (
      <div
        ref={drag.pillRef}
        style={{ left: drag.pos.x, top: drag.pos.y }}
        className="fixed z-[52] touch-none select-none"
        onPointerDown={drag.onPointerDown}
        onPointerMove={drag.onPointerMove}
        onPointerUp={drag.onPointerUp}
      >
        <button
          type="button"
          onClick={() => {
            // Only open card if user didn't drag
            if (!drag.hasMoved.current) setCollapsed(false);
          }}
          className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-gray-800 rounded-full shadow-lg dark:shadow-black/40 border border-gray-200/80 dark:border-gray-700/60 hover:shadow-xl transition-shadow cursor-grab active:cursor-grabbing"
        >
          <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
            <MapPin size={13} className="text-emerald-600 dark:text-emerald-400" />
          </div>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
            Enable location
          </span>
        </button>
      </div>
    );
  }

  // ── Full card ──
  return (
    <div className="fixed bottom-20 left-3 right-3 z-[52] animate-slide-up">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl dark:shadow-black/40 overflow-hidden border border-gray-200/80 dark:border-gray-700/60">
        <div className="bg-gradient-to-r from-emerald-500 to-cyan-500 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logos/coordal-logo-white.svg" alt="" className="w-6 h-6" />
            <span className="text-white font-semibold text-sm">Coordal Maps</span>
          </div>
          <button
            type="button"
            onClick={handleNotNow}
            className="p-0.5 text-white/70 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4">
          {permState === 'unsupported' ? (
            <>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Not supported</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Your browser does not support location access. Try opening the map in Chrome or Safari.
              </p>
              <div className="flex gap-2 mt-4">
                <button type="button" onClick={handleNotNow}
                  className="flex-1 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors">
                  Dismiss
                </button>
              </div>
            </>
          ) : permState === 'unavailable' ? (
            <>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Location unavailable</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Your device could not determine your location. Check that GPS or network location is enabled, then try again.
              </p>
              <div className="flex gap-2 mt-4">
                <button type="button" onClick={handleNotNow}
                  className="flex-1 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors">
                  Not now
                </button>
                <button type="button" onClick={handleEnableLocation} disabled={locating}
                  className="flex-1 py-2.5 text-sm font-medium bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-xl flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity shadow-sm disabled:opacity-60">
                  {locating ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Retrying…</> : 'Retry'}
                </button>
              </div>
            </>
          ) : permState === 'denied' ? (
            <>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                Location access blocked
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                To use nearby features, update your browser settings:
              </p>
              <div className="space-y-2">
                {LOCATION_INSTRUCTIONS[detectPlatform()].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400">{i + 1}</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{item.step}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={handleNotNow}
                  className="flex-1 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                >
                  Not now
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Enable location
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Center the map on you for nearby places
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={handleNotNow}
                  className="flex-1 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                >
                  Not now
                </button>
                <button
                  type="button"
                  onClick={handleEnableLocation}
                  disabled={locating}
                  className="flex-1 py-2.5 text-sm font-medium bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-xl flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity shadow-sm disabled:opacity-60"
                >
                  {locating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Locating…
                    </>
                  ) : (
                    <>
                      <Navigation size={14} />
                      Enable Location
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
